import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiUser, 
  FiSettings, 
  FiVideo, 
  FiBell, 
  FiShield, 
  FiCreditCard, 
  FiGlobe,
  FiMonitor
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext.js';

const Settings = () => {
  const { currentUser } = useAuth();
  const [activeSection, setActiveSection] = useState('general');

  // Settings categories
  const categories = [
    { 
      id: 'general', 
      name: 'General Settings', 
      icon: <FiSettings className="text-2xl" />,
      description: 'Configure basic account preferences and site appearance'
    },
    { 
      id: 'profile', 
      name: 'Profile Settings', 
      icon: <FiUser className="text-2xl" />,
      description: 'Manage your public profile information and visibility'
    },
    { 
      id: 'stream', 
      name: 'Stream Settings', 
      icon: <FiVideo className="text-2xl" />,
      description: 'Configure stream quality, chat, and broadcasting options'
    },
    { 
      id: 'notifications', 
      name: 'Notifications', 
      icon: <FiBell className="text-2xl" />,
      description: 'Control email and push notification preferences'
    },
    { 
      id: 'privacy', 
      name: 'Privacy & Security', 
      icon: <FiShield className="text-2xl" />,
      description: 'Manage privacy settings and account security options'
    },
    { 
      id: 'billing', 
      name: 'Billing & Subscriptions', 
      icon: <FiCreditCard className="text-2xl" />,
      description: 'Manage payment methods and subscription details'
    },
    { 
      id: 'appearance', 
      name: 'Appearance', 
      icon: <FiMonitor className="text-2xl" />,
      description: 'Customize the look and feel of your experience'
    },
    { 
      id: 'language', 
      name: 'Language & Region', 
      icon: <FiGlobe className="text-2xl" />,
      description: 'Set language preferences and regional settings'
    }
  ];

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-harmony-blue p-6">
        <div className="max-w-4xl mx-auto bg-harmony-blue-dark p-8 rounded-lg shadow-lg">
          <h1 className="text-2xl font-bold text-harmony-white mb-4">Settings</h1>
          <p className="text-harmony-white/70">Please log in to access your settings.</p>
          <Link to="/login" className="mt-4 inline-block bg-harmony-orange text-harmony-white px-4 py-2 rounded hover:bg-harmony-orange/90">
            Log In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-harmony-blue p-6">
      <div className="max-w-6xl mx-auto">
        <motion.h1 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-3xl font-bold text-harmony-white mb-6"
        >
          Settings
        </motion.h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Settings Categories */}
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {categories.map((category) => (
              <motion.div
                key={category.id}
                whileHover={{ y: -5 }}
                className={`bg-harmony-blue-dark rounded-lg p-6 border ${
                  activeSection === category.id 
                    ? 'border-harmony-orange shadow-lg' 
                    : 'border-harmony-white/10'
                } cursor-pointer transition-all`}
                onClick={() => setActiveSection(category.id)}
              >
                <div className={`mb-4 ${
                  activeSection === category.id
                    ? 'text-harmony-orange'
                    : 'text-harmony-white/70'
                }`}>
                  {category.icon}
                </div>
                <h3 className="text-harmony-white font-medium text-lg mb-2">{category.name}</h3>
                <p className="text-harmony-white/50 text-sm">{category.description}</p>
              </motion.div>
            ))}
          </div>
          
          {/* Settings Content */}
          <div className="lg:col-span-3">
            <div className="bg-harmony-blue-dark rounded-lg p-6 shadow-lg border border-harmony-white/10">
              {activeSection === 'general' && (
                <div>
                  <h2 className="text-2xl font-bold text-harmony-white mb-4">General Settings</h2>
                  <p className="text-harmony-white/70 mb-6">Configure basic account preferences and site appearance.</p>
                  
                  <Link 
                    to="/settings/general" 
                    className="bg-harmony-orange text-harmony-white px-4 py-2 rounded hover:bg-harmony-orange/90"
                  >
                    Go to General Settings
                  </Link>
                </div>
              )}
              
              {activeSection === 'profile' && (
                <div>
                  <h2 className="text-2xl font-bold text-harmony-white mb-4">Profile Settings</h2>
                  <p className="text-harmony-white/70 mb-6">Manage your public profile information and visibility.</p>
                  
                  <Link 
                    to="/settings/profile" 
                    className="bg-harmony-orange text-harmony-white px-4 py-2 rounded hover:bg-harmony-orange/90"
                  >
                    Go to Profile Settings
                  </Link>
                </div>
              )}
              
              {activeSection === 'stream' && (
                <div>
                  <h2 className="text-2xl font-bold text-harmony-white mb-4">Stream Settings</h2>
                  <p className="text-harmony-white/70 mb-6">Configure stream quality, chat, and broadcasting options.</p>
                  
                  <Link 
                    to="/stream-settings" 
                    className="bg-harmony-orange text-harmony-white px-4 py-2 rounded hover:bg-harmony-orange/90"
                  >
                    Go to Stream Settings
                  </Link>
                </div>
              )}
              
              {activeSection === 'notifications' && (
                <div>
                  <h2 className="text-2xl font-bold text-harmony-white mb-4">Notification Settings</h2>
                  <p className="text-harmony-white/70 mb-6">Control email and push notification preferences.</p>
                  
                  <div className="text-harmony-white/70">Notification settings are coming soon.</div>
                </div>
              )}
              
              {activeSection === 'privacy' && (
                <div>
                  <h2 className="text-2xl font-bold text-harmony-white mb-4">Privacy & Security</h2>
                  <p className="text-harmony-white/70 mb-6">Manage privacy settings and account security options.</p>
                  
                  <div className="text-harmony-white/70">Privacy settings are coming soon.</div>
                </div>
              )}
              
              {activeSection === 'billing' && (
                <div>
                  <h2 className="text-2xl font-bold text-harmony-white mb-4">Billing & Subscriptions</h2>
                  <p className="text-harmony-white/70 mb-6">Manage payment methods and subscription details.</p>
                  
                  <div className="text-harmony-white/70">Billing settings are coming soon.</div>
                </div>
              )}
              
              {activeSection === 'appearance' && (
                <div>
                  <h2 className="text-2xl font-bold text-harmony-white mb-4">Appearance Settings</h2>
                  <p className="text-harmony-white/70 mb-6">Customize the look and feel of your experience.</p>
                  
                  <div className="text-harmony-white/70">Appearance settings are coming soon.</div>
                </div>
              )}
              
              {activeSection === 'language' && (
                <div>
                  <h2 className="text-2xl font-bold text-harmony-white mb-4">Language & Region</h2>
                  <p className="text-harmony-white/70 mb-6">Set language preferences and regional settings.</p>
                  
                  <div className="text-harmony-white/70">Language settings are coming soon.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 